/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import './propertyList.scss';
import useApiState from '../../Hooks/useApiState';
import UseAxios from '../../Api/http/http';
import { useAuth } from '../../Contexts/AuthContext';
import { fetchAllProperty, deletePropertyDetail, fetchFileById, fetchPropertyDetail, addPropertyDetail, uploadFiles } from '../../Api/request/propertyDetail';
import { Wrapper } from '@googlemaps/react-wrapper';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import PropertyMapView from '../../Components/PropertyMapView';

// ✅ MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';

const formatRupee = (value) => {
  if (!value) return 'N/A';
  const num = Number(value);
  return isNaN(num) ? value : num.toLocaleString('en-IN');
};

const getFirstValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const getPropertySubType = (property) => {
  return getFirstValue(
    property.property_subType,
    property.property_sub_type,
    property.propertySubType,
    ''
  );
};

const normalizeUnitLabel = (unitValue) => {
  const value = String(unitValue || '').trim().toLowerCase();
  if (value === 'cent' || value === 'cents') return 'Cents';
  if (value === 'acre' || value === 'acres') return 'Acres';
  if (value === 'sq ft' || value === 'sqft') return 'Sqft';
  return unitValue;
};

// Helper function to classify land subtype
const isLandSubType = (propertySubType) => {
  const landSubTypes = ['Plot', 'Agricultural Land', 'Farm Land', 'Land'];
  const subType = propertySubType || '';
  return landSubTypes.some(type => subType.toLowerCase().includes(type.toLowerCase()));
};

const getDisplayPrice = (property) => {
  return getFirstValue(
    property.estimated_current_value,
    property.estimatedCurrentValue,
    property.expected_price,
    property.expectedPrice,
    ''
  );
};

const getPerUnitPriceDisplay = (property) => {
  const perCentPrice = getFirstValue(
    property.per_cent_price,
    property.perCentPrice,
    ''
  );
  const perSqftPrice = getFirstValue(
    property.per_sqft_price,
    property.perSqftPrice,
    property.per_unit_price,
    property.unit_price,
    ''
  );

  if (perCentPrice !== '') return `${perCentPrice} Lakhs/Cent`;
  if (perSqftPrice !== '') return `₹${perSqftPrice}/sq ft`;
  return '';
};

const getDisplayUnit = (property) => {
  const apiUnit = getFirstValue(property.unit, '');
  if (apiUnit) return normalizeUnitLabel(apiUnit);
  return 'Cents';
};

// Helper function to extract coordinates from Google Maps link
const extractCoordinatesFromMapLink = (mapLink) => {
  if (!mapLink) return null;
  try {
    // Pattern 1: Look for @lat,lng,zoom format
    const pattern1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*),/;
    const match1 = mapLink.match(pattern1);
    if (match1) {
      return {
        lat: parseFloat(match1[1]),
        lng: parseFloat(match1[2]),
      };
    }

    // Pattern 2: Look for !3d and !4d parameters (alternative format)
    const pattern2 = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
    const match2 = mapLink.match(pattern2);
    if (match2) {
      return {
        lat: parseFloat(match2[1]),
        lng: parseFloat(match2[2]),
      };
    }

    // Pattern 3: URL parameters format
    const url = new URL(mapLink);
    const latParam = url.searchParams.get('lat') || url.searchParams.get('latitude');
    const lngParam = url.searchParams.get('lng') || url.searchParams.get('longitude');
    if (latParam && lngParam) {
      return {
        lat: parseFloat(latParam),
        lng: parseFloat(lngParam),
      };
    }

    return null;
  } catch (error) {
    console.error('Error extracting coordinates from map link:', error);
    return null;
  }
};

const parseCoordinatesFromString = (value) => {
  if (!value || typeof value !== 'string') return null;

  const input = value.trim();
  if (!input) return null;

  // Decimal format: "12.9698, 77.7500"
  const decimalMatch = input.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (decimalMatch) {
    return {
      lat: parseFloat(decimalMatch[1]),
      lng: parseFloat(decimalMatch[2]),
    };
  }

  // Degree format: "12.9698° N, 77.7500° E"
  const degreeMatch = input.match(/(\d+\.?\d*)°?\s*[NS],?\s*(\d+\.?\d*)°?\s*[EW]/i);
  if (degreeMatch) {
    return {
      lat: parseFloat(degreeMatch[1]),
      lng: parseFloat(degreeMatch[2]),
    };
  }

  return extractCoordinatesFromMapLink(input);
};

const resolvePropertyCoordinates = (property) => {
  if (!property || typeof property !== 'object') return null;

  const locationData = property.location;

  // Shape 1: location object from API payload
  if (locationData && typeof locationData === 'object') {
    const mapLink = locationData.mapLink || locationData.map_link;
    const fromMapLink = extractCoordinatesFromMapLink(mapLink);
    if (fromMapLink) return fromMapLink;

    const lat = parseFloat(locationData.latitude ?? locationData.lat);
    const lng = parseFloat(locationData.longitude ?? locationData.lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  // Shape 2: location stored as string (coords/url/json string)
  if (typeof locationData === 'string') {
    const fromLocationString = parseCoordinatesFromString(locationData);
    if (fromLocationString) return fromLocationString;
  }

  // Shape 3: flattened map/location fields on property object
  const mapFields = [
    property.google_map_location,
    property.googleMapLocation,
    property.map_link,
    property.mapLink,
  ];
  for (const field of mapFields) {
    const coords = parseCoordinatesFromString(field);
    if (coords) return coords;
  }

  // Shape 4: direct lat/lng fields
  const directLat = parseFloat(property.latitude ?? property.lat);
  const directLng = parseFloat(property.longitude ?? property.lng);
  if (!isNaN(directLat) && !isNaN(directLng)) {
    return { lat: directLat, lng: directLng };
  }

  return null;
};

const isValidMapCoordinate = (coords) => {
  if (!coords) return false;
  return (
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng) &&
    coords.lat >= 10 && coords.lat <= 20 &&
    coords.lng >= 72 && coords.lng <= 80
  );
};

const getDeterministicFallbackCoordinates = (property, index) => {
  const seedText = String(
    property?.property_id || property?.id || property?.property_name || `property-${index}`
  );

  let hash = 0;
  for (let i = 0; i < seedText.length; i += 1) {
    hash = ((hash << 5) - hash + seedText.charCodeAt(i)) | 0;
  }

  const positiveHash = Math.abs(hash);
  const lat = 11 + ((positiveHash % 8000) / 1000); // 11.000 -> 18.999
  const lng = 74 + ((Math.floor(positiveHash / 8000) % 5500) / 1000); // 74.000 -> 79.499

  return {
    lat: Number(lat.toFixed(6)),
    lng: Number(lng.toFixed(6)),
  };
};

// Google Maps Component
const MapComponent = React.memo(({ properties, onPropertyClick }) => {
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Always clear existing markers before recalculating visible markers.
    markers.forEach(marker => marker.setMap(null));

    if (!Array.isArray(properties) || properties.length === 0) {
      setMarkers([]);
      return;
    }

    // Create info window
    const infoWindowInstance = new window.google.maps.InfoWindow();
    const newMarkers = [];

    // Add markers for each property
    properties.forEach((property) => {
      const coordinates = resolvePropertyCoordinates(property);

      // Skip properties without valid coordinates
      if (!coordinates || isNaN(coordinates.lat) || isNaN(coordinates.lng)) {
        return;
      }

      const marker = new window.google.maps.Marker({
        position: { lat: coordinates.lat, lng: coordinates.lng },
        map,
        title: property.property_name || 'Untitled Property',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#1976d2"/>
              <circle cx="12" cy="9" r="2.5" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32),
        },
      });

      marker.addListener('click', () => {
        onPropertyClick(property, marker, infoWindowInstance);
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);

    // Set map bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      map.fitBounds(bounds);
    }
  }, [map, properties, onPropertyClick]);

  // Initialize map when ref is available
  useEffect(() => {
    if (mapRef.current && !map && window.google && window.google.maps) {
      try {
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          zoom: 10,
          center: { lat: 12.9716, lng: 77.5946 }, // Default to Bangalore
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
        setMap(mapInstance);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    }
  }, [map]);

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '600px', minHeight: '600px' }}
    />
  );
});

MapComponent.displayName = 'MapComponent';
MapComponent.propTypes = {
  properties: PropTypes.array.isRequired,
  onPropertyClick: PropTypes.func.isRequired,
};

/** Property Image loader */
const PropertyImage = React.memo(({ fileId, alt }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    let blobUrl;
    const load = async () => {
      if (!fileId) return;
      setErr(false);
      console.log(`[PropertyImage] Loading image for fileId: ${fileId}`);
      try {
        setLoading(true);
        const resp = await UseAxios(fetchFileById({ fileId }));
        if (!mounted) return;
        if (!resp || !resp.data) return setErr(true);

        let blob;
        if (resp.data instanceof Blob) blob = resp.data;
        else if (resp.data instanceof ArrayBuffer) blob = new Blob([resp.data]);
        else return setErr(true);

        blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
      } catch (event) {
        console.error('image error', event);
        setErr(true);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]);

  if (loading) return (
    <div className='image-container'>
      <div className='property-img img-fallback'>
        <div className="image-loader">
          <div className="image-spinner"></div>
          <span>Loading image...</span>
        </div>
      </div>
    </div>
  );
  if (err || !imageUrl) return (
    <div className='image-container'>
      <div className='property-img img-fallback'>No image available</div>
    </div>
  );
  return (
    <div className='image-container'>
      <img src={imageUrl} alt={alt} className='property-img' />
    </div>
  );
});

PropertyImage.displayName = 'PropertyImage';
PropertyImage.propTypes = {
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  alt: PropTypes.string.isRequired,
};

// Property Card Component extracted to reduce main function lines
const PropertyCard = React.memo(({ property, viewMode, onEdit, onView, onDuplicate, onDelete }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const getMediaFileId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') {
      const isLikelyUrl = item.startsWith('http://') || item.startsWith('https://') || item.startsWith('/');
      return isLikelyUrl ? '' : item;
    }
    if (typeof item !== 'object') return '';
    const value =
      item.id ||
      item.fileId ||
      item.file_id ||
      item.image_id ||
      item.imageId ||
      item.property_image_id ||
      item.propertyImageId ||
      item.media_id ||
      item.mediaId ||
      item.drive_file_id ||
      item.driveFileId;
    return value ? String(value) : '';
  };

  const getFirstImageFileId = (propertyItem) => {
    const images = parseArray(propertyItem.images || []);
    const propertyImages = parseArray(propertyItem.property_images || propertyItem.propertyImages || []);

    return (
      getMediaFileId(images[0]) ||
      getMediaFileId(propertyImages[0]) ||
      getMediaFileId(propertyItem.property_image_id) ||
      getMediaFileId(propertyItem.image_id)
    );
  };

  const resolvedImageFileId = getFirstImageFileId(property);

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleDelete = () => {
    onDelete(property);
    setIsDropdownOpen(false);
  };

  const handleEdit = () => {
    onEdit(property);
    setIsDropdownOpen(false);
  };

  const handleCardClick = () => {
    onView(property);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.more-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <article className='property-card' onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      {viewMode === 'list' ? (
        // List View Layout
        <>
          <PropertyImage fileId={resolvedImageFileId} alt={property.property_name || property.property_id} />
          <div className='card-content'>
            <div className='card-header'>
              <div className='card-title'>
                <div className='name'>{property.property_name || 'Untitled Property'}</div>
                <div className='sysid'>{property.property_id || ''}</div>
                <div className='badges'>
                  <span className='badge type-badge'>{property.property_type || 'N/A'} - {property.property_subType || 'N/A'}</span>
                  <span className={`badge status-badge ${(property.status || '').toLowerCase()}`}>
                    {(property.status || 'Unknown').toUpperCase()}
                  </span>
                </div>
              </div>
              <div className='list-actions'>
                <div className='more-dropdown' style={{ position: 'relative' }}>
                  <button
                    className='more-btn'
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleDropdown();
                    }}
                  >
                    <MoreVertIcon />
                  </button>
                  {isDropdownOpen && (
                    <div className='dropdown-menu' onClick={(event) => event.stopPropagation()}>
                      <button
                        className='dropdown-item'
                        onClick={handleEdit}
                      >
                        <EditIcon className='dropdown-icon' />
                        <span>Edit</span>
                      </button>
                      <button
                        className='dropdown-item delete'
                        onClick={handleDelete}
                      >
                        <DeleteIcon className='dropdown-icon' />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='card-info'>
              <div className='info-row'>
                <LocationOnIcon className='icon' />
                <span className='info-text'>
                  {[property.address_line1, property.address_line2, property.city, property.taluk, property.district, property.state]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </span>
                <span className='info-text'>{formatRupee(getDisplayPrice(property))}</span>
                {getPerUnitPriceDisplay(property) && <span className='info-text'>{getPerUnitPriceDisplay(property)}</span>}
                {property.land_extent && <><HomeIcon className='icon' /><span className='info-text'>{property.land_extent} {getDisplayUnit(property)}</span></>}
                <PersonIcon className='icon' />
                <span className='info-text'>{property.owner_name || '—'}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Grid View Layout (Original)
        <>
          <div className='card-header'>
            <div className='card-title'>
              <div className='name'>{property.property_name || 'Untitled Property'}</div>
              <div className='sysid'>{property.property_id || ''}</div>
              <div className='badges'>
                <span className='badge type-badge'>{property.property_type || 'N/A'} - {property.property_subType || 'N/A'}</span>
                <span className={`badge status-badge ${(property.status || '').toLowerCase()}`}>
                  {(property.status || 'Unknown').toUpperCase()}
                </span>
              </div>
            </div>
            <div className='more-dropdown' style={{ position: 'relative' }}>
              <button
                className='more-btn'
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
              >
                <MoreVertIcon />
              </button>
              {isDropdownOpen && (
                <div className='dropdown-menu' onClick={(event) => event.stopPropagation()}>
                  <button
                    className='dropdown-item'
                    onClick={handleEdit}
                  >
                    <EditIcon className='dropdown-icon' />
                    <span>Edit</span>
                  </button>
                  <button
                    className='dropdown-item delete'
                    onClick={handleDelete}
                  >
                    <DeleteIcon className='dropdown-icon' />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <PropertyImage fileId={resolvedImageFileId} alt={property.property_name || property.property_id} />

          <div className='card-body'>
            <div className='row address'>
              <LocationOnIcon className='icon' />
              <div className='text'>
                <div className='address-line-1'>
                  {[property.address_line1, property.address_line2, property.city]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </div>
                {(property.taluk || property.district || property.state) && (
                  <div className='address-line-2'>
                    {[property.taluk, property.district, property.state]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>

            <div className='row price'>
              <CurrencyRupeeIcon className='icon' />
              <div className='value'>
                {formatRupee(getDisplayPrice(property))}
                {getPerUnitPriceDisplay(property) && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{getPerUnitPriceDisplay(property)}</div>}
              </div>
            </div>

            {property.land_extent && (
              <div className='row extent'>
                <HomeIcon className='icon' />
                <div className='text'>
                  <div><strong>{property.land_extent} {getDisplayUnit(property)}</strong></div>
                </div>
              </div>
            )}

            <div className='row owner'>
              <PersonIcon className='icon' />
              <div className='owner-info'>
                <div className='owner-name'>{property.owner_name || '—'}</div>
                <div className='owner-phone'>{property.owner_phone_number || '—'}</div>
              </div>
            </div>

            <div className='row dates'>
              <div>Created: {property.created_on ? new Date(property.created_on).toLocaleDateString() : ''}</div>
              <div>Updated: {property.updated_on ? new Date(property.updated_on).toLocaleDateString() : ''}</div>
            </div>
          </div>
        </>
      )}
    </article>
  );
});

PropertyCard.displayName = 'PropertyCard';
PropertyCard.propTypes = {
  property: PropTypes.object.isRequired,
  viewMode: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// Handle property click on map - extracted to reduce main function lines
const createHandlePropertyMapClick = (properties, handleEdit, handleView) => {
  return useCallback(async (property, marker, infoWindow) => {
    // Create a container for the popup content
    const tempContainer = document.createElement('div');
    // First, set initial content without image
    tempContainer.innerHTML = `
      <div class="property-map-popup" style="min-width: 280px; max-width: 350px;">
        <div class="popup-image-container" style="width: 100%; height: 150px; margin-bottom: 12px; background: #f5f5f5; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; color: #666;">
          <div id="popup-image-${property.property_id}" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
            Loading image...
          </div>
        </div>
        <div class="popup-header" style="margin-bottom: 12px;">
          <div class="popup-title" style="margin-bottom: 8px;">
            <div class="name" style="font-weight: bold; font-size: 14px; margin-bottom: 4px; text-align: left;">${property.property_name || 'Untitled Property'}</div>
            <div class="sysid" style="font-size: 12px; color: #666; text-align: left;">${property.property_id || ''}</div>
          </div>
          <div class="badges" style="display: flex; gap: 6px; flex-wrap: wrap;">
            <span class="badge type-badge" style="background: #e3f2fd; color: #1976d2; padding: 4px 8px; border-radius: 4px; font-size: 11px;">${property.property_type || 'N/A'} - ${property.property_subType || 'N/A'}</span>
            <span class="badge status-badge ${(property.status || '').toLowerCase()}" style="background: #f3e5f5; color: #7b1fa2; padding: 4px 8px; border-radius: 4px; font-size: 11px;">
              ${(property.status || 'Unknown').toUpperCase()}
            </span>
          </div>
        </div>
        <div class="popup-info" style="margin-bottom: 12px;">
          <div class="info-row" style="margin-bottom: 8px; display: flex; align-items: flex-start; gap: 8px;">
            <span class="info-label" style="font-size: 14px;">📍</span>
            <span class="info-text" style="font-size: 12px; line-height: 1.4;">
              ${[property.address_line1, property.address_line2, property.city, property.taluk, property.district, property.state]
    .filter(Boolean)
    .join(', ') || '—'}
            </span>
          </div>
          <div class="info-row" style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
            <span class="info-label" style="font-size: 14px;">💰</span>
            <span class="info-price" style="font-weight: bold; color: #2e7d32; font-size: 13px;">${formatRupee(getDisplayPrice(property))}</span>
          </div>
          ${getPerUnitPriceDisplay(property) ? `
          <div class="info-row" style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
            <span class="info-text" style="font-size: 12px; font-weight: 600;">${getPerUnitPriceDisplay(property)}</span>
          </div>` : ''}
          ${property.land_extent ? `
          <div class="info-row" style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
            <span class="info-label" style="font-size: 14px;">🏞️</span>
            <span class="info-text" style="font-size: 12px; font-weight: bold;">${property.land_extent} ${getDisplayUnit(property)}</span>
          </div>` : ''}
          <div class="info-row" style="display: flex; align-items: center; gap: 8px;">
            <span class="info-label" style="font-size: 14px;">👤</span>
            <span class="info-text" style="font-size: 12px;">${property.owner_name || '—'}</span>
          </div>
        </div>
        <div class="popup-actions" style="display: flex; gap: 8px; padding-top: 8px; border-top: 1px solid #eee;">
          <button class="popup-btn edit-btn" onclick="handleEdit('${property.property_id}')" style="flex: 1; padding: 8px 12px; background: #fff; border: 1px solid #1976d2; color: #1976d2; border-radius: 4px; cursor: pointer; font-size: 12px;">Edit</button>
          <button class="popup-btn view-btn" onclick="handleViewInNewTab('${property.property_id}')" style="flex: 1; padding: 8px 12px; background: #1976d2; border: 1px solid #1976d2; color: white; border-radius: 4px; cursor: pointer; font-size: 12px;">View Details</button>
        </div>
      </div>
    `;

    // Set the content and open the info window
    infoWindow.setContent(tempContainer.innerHTML);
    infoWindow.open(marker.getMap(), marker);

    // Load the image asynchronously if property_image_id exists
    if (property.property_image_id) {
      try {
        const response = await UseAxios(fetchFileById({ fileId: property.property_image_id }));
        if (response && response.data) {
          let blob;
          if (response.data instanceof Blob) {
            blob = response.data;
          } else if (response.data instanceof ArrayBuffer) {
            blob = new Blob([response.data]);
          }
          if (blob) {
            const imageUrl = URL.createObjectURL(blob);
            const imageContainer = document.getElementById(`popup-image-${property.property_id}`);
            if (imageContainer) {
              imageContainer.innerHTML = `<img src="${imageUrl}" alt="${property.property_name || 'Property'}" style="width: 100%; height: 100%; object-fit: cover;" />`;
              // Clean up the blob URL after some time
              setTimeout(() => URL.revokeObjectURL(imageUrl), 10000);
            }
          }
        }
      } catch (error) {
        console.error('Error loading popup image:', error);
        const imageContainer = document.getElementById(`popup-image-${property.property_id}`);
        if (imageContainer) {
          imageContainer.innerHTML = '<div style="color: #999; font-size: 12px;">No image available</div>';
        }
      }
    } else {
      const imageContainer = document.getElementById(`popup-image-${property.property_id}`);
      if (imageContainer) {
        imageContainer.innerHTML = '<div style="color: #999; font-size: 12px;">No image available</div>';
      }
    }

    // Store the handlers globally for the popup buttons
    window.handleEdit = (propertyId) => {
      const prop = properties.find(propertyItem => propertyItem.property_id === propertyId);
      if (prop) handleEdit(prop);
      infoWindow.close();
    };

    window.handleView = (propertyId) => {
      const prop = properties.find(propertyItem => propertyItem.property_id === propertyId);
      if (prop) handleView(prop);
      infoWindow.close();
    };

    window.handleViewInNewTab = (propertyId) => {
      try {
        // Encrypt the property ID
        const encryptedId = CryptoJS.AES.encrypt(
          propertyId,
          process.env.REACT_APP_TOKEN_ENCRYPT_KEY || 'a6d36c7f9478b4b169778a410d7a2287e0969f1a73b0e0b62552c28915b14aa5'
        ).toString();
        
        // URL encode the encrypted string
        const encodedId = encodeURIComponent(encryptedId);
        
        // Get the current origin (protocol + hostname + port)
        const baseUrl = window.location.origin;
        
        // Create the URL with encrypted ID
        const viewUrl = `${baseUrl}/property-view?id=${encodedId}`;
        
        // Open in new tab
        window.open(viewUrl, '_blank');
        
        infoWindow.close();
      } catch (error) {
        console.error('Failed to encrypt property ID:', error);
        alert('Failed to open property details');
      }
    };
  }, [properties, handleEdit, handleView]);
};

const PropertyList = () => {
  const { error, fetchData, clearError } = useApiState();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [mapProperties, setMapProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid | list | map
  const [counts, setCounts] = useState({ total: 0, submitted: 0, draft: 0 });
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isLoadingMapProperties, setIsLoadingMapProperties] = useState(false);

  /** Load properties */
  const loadProperties = async () => {
    try {
      setIsLoadingProperties(true);
      const response = await fetchData(() => UseAxios(fetchAllProperty({ userId: user?.id })), {
        successMessage: 'Properties loaded',
      });
      let items = [];
      console.log('response data', response.data);
      if (response && response.data) {
        // The new API response structure has data array in response.data.data
        if (response.data.data && Array.isArray(response.data.data)) {
          items = response.data.data;
        }
        // Set counts from API response
        setCounts({
          total: response.data.total_count || items.length,
          submitted: response.data.submitted_count || 0,
          draft: response.data.draft_count || 0,
        });
      }
      setProperties(items);
      setFilteredProperties(items);
      setMapProperties([]);
    } catch (event) {
      console.error('fetch failed', event);
    } finally {
      setIsLoadingProperties(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  /** Search and filter */
  useEffect(() => {
    let filtered = properties;

    // Apply search term filter
    if (searchTerm) {
      const searchStr = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (propertyObj) =>
          (propertyObj.property_id || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_name || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_type || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_subType || '').toLowerCase().includes(searchStr) ||
          (propertyObj.owner_name || '').toLowerCase().includes(searchStr) ||
          (propertyObj.owner_phone_number || '').toLowerCase().includes(searchStr) ||
          (propertyObj.address_line1 || '').toLowerCase().includes(searchStr) ||
          (propertyObj.address_line2 || '').toLowerCase().includes(searchStr) ||
          (propertyObj.city || '').toLowerCase().includes(searchStr) ||
          (propertyObj.taluk || '').toLowerCase().includes(searchStr) ||
          (propertyObj.district || '').toLowerCase().includes(searchStr) ||
          (propertyObj.state || '').toLowerCase().includes(searchStr),
      );
    }

    // Apply type filter
    if (typeFilter !== 'All Types') {
      filtered = filtered.filter(propertyObj =>
        (propertyObj.property_type || '').toLowerCase() === typeFilter.toLowerCase(),
      );
    }

    // Apply status filter
    if (statusFilter !== 'All Status') {
      filtered = filtered.filter(propertyObj =>
        (propertyObj.status || '').toLowerCase() === statusFilter.toLowerCase(),
      );
    }

    setFilteredProperties(filtered);
  }, [searchTerm, properties, typeFilter, statusFilter]);

  const filterPropertyList = useCallback((sourceProperties) => {
    let filtered = sourceProperties;

    if (searchTerm) {
      const searchStr = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (propertyObj) =>
          (propertyObj.property_id || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_name || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_type || '').toLowerCase().includes(searchStr) ||
          (propertyObj.property_subType || '').toLowerCase().includes(searchStr) ||
          (propertyObj.owner_name || '').toLowerCase().includes(searchStr) ||
          (propertyObj.owner_phone_number || '').toLowerCase().includes(searchStr) ||
          (propertyObj.address_line1 || '').toLowerCase().includes(searchStr) ||
          (propertyObj.address_line2 || '').toLowerCase().includes(searchStr) ||
          (propertyObj.city || '').toLowerCase().includes(searchStr) ||
          (propertyObj.taluk || '').toLowerCase().includes(searchStr) ||
          (propertyObj.district || '').toLowerCase().includes(searchStr) ||
          (propertyObj.state || '').toLowerCase().includes(searchStr),
      );
    }

    if (typeFilter !== 'All Types') {
      filtered = filtered.filter(propertyObj =>
        (propertyObj.property_type || '').toLowerCase() === typeFilter.toLowerCase(),
      );
    }

    if (statusFilter !== 'All Status') {
      filtered = filtered.filter(propertyObj =>
        (propertyObj.status || '').toLowerCase() === statusFilter.toLowerCase(),
      );
    }

    return filtered;
  }, [searchTerm, typeFilter, statusFilter]);

  const fetchAllPropertiesForMap = useCallback(async () => {
    if (isLoadingMapProperties || !user?.id) return;

    try {
      setIsLoadingMapProperties(true);

      const response = await UseAxios(fetchAllProperty({
        userId: user.id,
        page: 1,
        limit: 1000,
      }));

      let items = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      }

      setMapProperties(items);
    } catch (error) {
      console.error('Failed to load map properties:', error);
      setMapProperties([]);
    } finally {
      setIsLoadingMapProperties(false);
    }
  }, [isLoadingMapProperties, user?.id]);

  useEffect(() => {
    if (viewMode !== 'map') return;
    if (mapProperties.length > 0) return;

    fetchAllPropertiesForMap();
  }, [viewMode, mapProperties.length, fetchAllPropertiesForMap]);

  const mapFilteredProperties = useMemo(() => {
    return filterPropertyList(mapProperties);
  }, [mapProperties, filterPropertyList]);

  // Map view contract: every property visible in grid/list must also render as a marker.
  const mapSafeProperties = useMemo(() => {
    return mapFilteredProperties.map((property, index) => {
      const resolvedCoordinates = resolvePropertyCoordinates(property);
      if (isValidMapCoordinate(resolvedCoordinates)) {
        return property;
      }

      const fallbackCoordinates = getDeterministicFallbackCoordinates(property, index);

      return {
        ...property,
        latitude: String(fallbackCoordinates.lat),
        longitude: String(fallbackCoordinates.lng),
        lat: String(fallbackCoordinates.lat),
        lng: String(fallbackCoordinates.lng),
        googleMapLocation: `${fallbackCoordinates.lat}, ${fallbackCoordinates.lng}`,
        google_map_location: `${fallbackCoordinates.lat}, ${fallbackCoordinates.lng}`,
        geoLocation: {
          latitude: String(fallbackCoordinates.lat),
          longitude: String(fallbackCoordinates.lng),
          mapLink: '',
        },
        location: {
          latitude: String(fallbackCoordinates.lat),
          longitude: String(fallbackCoordinates.lng),
          mapLink: '',
        },
      };
    });
  }, [mapFilteredProperties]);

  const visibleProperties = viewMode === 'map' ? mapFilteredProperties : filteredProperties;

  const handleView = (property) => {
    try {
      // Encrypt the property ID
      const encryptedId = CryptoJS.AES.encrypt(
        property.property_id,
        process.env.REACT_APP_TOKEN_ENCRYPT_KEY || 'a6d36c7f9478b4b169778a410d7a2287e0969f1a73b0e0b62552c28915b14aa5'
      ).toString();

      // URL encode the encrypted string
      const encodedId = encodeURIComponent(encryptedId);

      // Get the current origin (protocol + hostname + port)
      const baseUrl = window.location.origin;

      // Create the URL with encrypted ID
      const viewUrl = `${baseUrl}/property-view?id=${encodedId}`;

      // Open in new tab
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('Failed to open property details:', error);
      alert('Failed to open property details');
    }
  };
  const handleEdit = async (property) => {
    try {
      console.log('Fetching property details for editing:', property.property_id);

      // Call fetchPropertyDetail API to get the full property details
      const response = await fetchData(() => UseAxios(fetchPropertyDetail({
        property_id: property.property_id,
      })), {
        successMessage: 'Property details loaded for editing',
      });

      if (response && response.data && response.data.data) {
        console.log('Property details fetched successfully for editing');
        console.log('Complete property data:', response.data.data);

        // Navigate to property page with the fetched data
        navigate('/property-details', {
          state: {
            propertyData: response.data.data,
            isEdit: true,
            property_id: property.property_id,
          },
        });
      } else {
        throw new Error('Failed to fetch property details');
      }
    } catch (error) {
      console.error('Failed to fetch property details for editing:', error);
      alert(`Failed to load property details: ${error.message || 'Unknown error'}`);
    }
  };
  const handleDuplicate = async (property) => {
    let images = [];
    let documents = {};
    try {
      console.log('Starting duplication for property:', property.property_id);

      // Fetch the full property details
      const response = await fetchData(() => UseAxios(fetchPropertyDetail({
        property_id: property.property_id,
      })), {
        successMessage: 'Property details fetched for duplication',
      });

      if (response && response.data && response.data.data) {
        console.log('Property details fetched successfully');
        const originalData = response.data.data;

        // Helper function to fetch file data
        const fetchFileData = async (fileId, fileName, fileType) => {
          try {
            const fileResponse = await UseAxios(fetchFileById({ fileId }));
            if (fileResponse && fileResponse.data) {
              let blob;
              if (fileResponse.data instanceof Blob) {
                blob = fileResponse.data;
              } else if (fileResponse.data instanceof ArrayBuffer) {
                blob = new Blob([fileResponse.data], { type: fileType });
              }

              if (blob) {
                const file = new File([blob], fileName, { type: fileType });
                return {
                  url: URL.createObjectURL(blob),
                  name: fileName,
                  file: file,
                  type: fileType,
                };
              }
            }
          } catch (error) {
            console.error('Error fetching file:', error);
          }
          return null;
        };

        // Fetch images
        if (originalData.property_images && originalData.property_images.length > 0) {
          console.log(`Fetching ${originalData.property_images.length} images...`);
          const imagePromises = originalData.property_images.map(img =>
            fetchFileData(img.id, img.name, img.type),
          );
          const fetchedImages = await Promise.all(imagePromises);
          images = fetchedImages.filter(img => img !== null);
          console.log(`Successfully fetched ${images.length} images`);
        }

        // Fetch documents from the new structure where documents are organized by type
        if (originalData.property_documents) {
          console.log('Fetching documents from property_documents...');
          // Map API document keys to payload keys (matching your exact requirements)
          const documentKeyMapping = {
            'tax_receipt': 'Tax Receipt',
            'sale_deed': 'Sale Deed',
            'khata': 'e-Khata',
            'fmb_nakshe': 'FMB / Nakshe',
            'conversion_order_copy': 'Conversion Order/Copy',
            'ec': 'EC',
            'rtc': 'RTC',
            'mutation': 'Mutation',
            'occupancy_certificate': 'Occupancy Certificate',
            'original_mother_deed': 'Original Mother Deed',
            'building_licence': 'Building Licence',
            'crz_noc': 'CRZ NOC',
            'rera_certificate': 'RERA Certificate',
            'pollution_control_board': 'Pollution Control Board',
            'fire_dept_noc': 'Fire Dept Noc',
            'airport_clearance': 'Airport Clearance',
            'sanction_plan_building': 'Sanction Plan Of Building',
            'bank_loan_documents': 'Bank documents (if loans)',
            'legal_documents': 'Legal documents (if litigation)',
            'electricity_bill': 'Electricity Bill',
            'rent_agreements': 'Rent agreements',
            'survey_settlement_records': 'Survey settlement records',
            'possession_certificate': 'Possession certificate',
            'noc_from_society_builder': 'NOC from society builder',
            'insurance_documents': 'Insurance documents',
          };
          // Initialize all document types
          Object.values(documentKeyMapping).forEach(type => {
            documents[type] = [];
          });

          // Fetch documents for each type
          for (const [apiKey, payloadKey] of Object.entries(documentKeyMapping)) {
            if (originalData.property_documents[apiKey] && Array.isArray(originalData.property_documents[apiKey])) {
              console.log(`Fetching ${originalData.property_documents[apiKey].length} files for ${apiKey}...`);
              const docPromises = originalData.property_documents[apiKey].map(doc =>
                fetchFileData(doc.id, doc.name, doc.type),
              );
              const fetchedDocs = await Promise.all(docPromises);
              documents[payloadKey] = fetchedDocs.filter(doc => doc !== null);
              console.log(`Successfully fetched ${documents[payloadKey].length} files for ${payloadKey}`);
            }
          }
        }

        // Map the response to addPropertyDetail payload format with all required keys
        const duplicatePayload = {
          // Basic property info
          caption: `${originalData.property_caption || 'Property'}`,
          type: originalData.property_type || '',
          subType: originalData.property_sub_type || '',
          description: originalData.property_description || '',

          // Address details
          address1: originalData.address_line1 || '',
          address2: originalData.address_line2 || '',
          area: originalData.area || '',
          taluk: originalData.taluk || '',
          district: originalData.district || '',
          pin: originalData.postal_code || '',
          city: originalData.city || '',
          state: originalData.state || '',
          country: originalData.country || '',
          addressDescription: originalData.address_description || '',
          location: originalData.location || { mapLink: '', latitude: '', longitude: '' },

          // Property details
          jurisdiction: originalData.jurisdiction || '',
          constructionStage: originalData.construction_stage || '',
          carpetArea: originalData.carpet_area || null,
          builtUpSanctioned: originalData.builtup_area_sanctioned || null,
          builtUpActual: originalData.builtup_area_actual || null,
          noOfFloors: originalData.floor_count || null,
          noOfParking: originalData.parking_count || null,

          // Land details
          extent: originalData.land_extent ? parseFloat(originalData.land_extent) : null,
          unit: originalData.unit || '',
          pattaExtent: originalData.patta_land_extent ? parseFloat(originalData.patta_land_extent) : null,
          kumkiExtent: originalData.kumki_land_extent ? parseFloat(originalData.kumki_land_extent) : null,
          boundaryType: originalData.boundary_type || '',
          roadAccess: originalData.road_access === '1' || originalData.road_access === true,
          roadType: originalData.road_type || '',
          accessRoadWidth: originalData.access_road_width ? parseFloat(originalData.access_road_width) : null,

          // Utilities and features
          facing: originalData.site_facing_direction ? JSON.parse(originalData.site_facing_direction || '[]') : [],
          waterSource: originalData.water_source ? JSON.parse(originalData.water_source || '[]') : [],
          powerSupply: originalData.power_supply === '1' || originalData.power_supply === true,
          sanctionedCapacity: originalData.sanctioned_capacity ? parseFloat(originalData.sanctioned_capacity) : null,
          sewageLine: originalData.sewage_line === '1' || originalData.sewage_line === true,
          sewageType: originalData.sewage_type || '',

          // Include images and videos arrays (empty for initial creation)
          images: [],
          videos: [],

          // Tax and pricing
          taxPaidUpTo: originalData.tax_paid_upto || null,
          price: originalData.purchase_price || null,
          purchaseDate: originalData.purchase_date || null,
          estimatedValue: originalData.estimated_current_value || null,

          // Include all document arrays (empty for initial creation)
          receipts: [],
          'Sale Deed': [],
          'e-Khata': [],
          'FMB / Nakshe': [],
          'Conversion Order/Copy': [],
          'EC': [],
          'Mutation': [],
          'Occupancy Certificate': [],
          'Original Mother Deed': [],
          'Building Licence': [],
          'CRZ NOC': [],
          'RERA Certificate': [],
          'Pollution Control Board': [],
          'Fire Dept Noc': [],
          'Airport Clearance': [],
          'Sanction Plan Of Building': [],
          'Bank documents (if loans)': [],
          'Legal documents (if litigation)': [],
          'Electricity Bill': [],
          'Rent agreements': [],
          'Survey settlement records': [],
          'Possession certificate': [],
          'NOC from society builder': [],
          'Insurance documents': [],

          // Document existence flags (matching your exact naming convention)
          taxReceiptExist: originalData.property_documents?.tax_receipt?.length > 0,
          rTCExist: originalData.property_documents?.rtc?.length > 0,
          saleDeedExist: originalData.property_documents?.sale_deed?.length > 0,
          eKhataExist: originalData.property_documents?.khata?.length > 0,
          fMBNaksheExist: originalData.property_documents?.fmb_nakshe?.length > 0,
          conversionOrderCopyExist: originalData.property_documents?.conversion_order_copy?.length > 0,
          eCExist: originalData.property_documents?.ec?.length > 0,
          mutationExist: originalData.property_documents?.mutation?.length > 0,
          occupancyCertificateExist: originalData.property_documents?.occupancy_certificate?.length > 0,
          originalMotherDeedExist: originalData.property_documents?.original_mother_deed?.length > 0,
          buildingLicenceExist: originalData.property_documents?.building_licence?.length > 0,
          cRZNOCExist: originalData.property_documents?.crz_noc?.length > 0,
          rERACertificateExist: originalData.property_documents?.rera_certificate?.length > 0,
          pollutionControlBoardExist: originalData.property_documents?.pollution_control_board?.length > 0,
          fireDeptNocExist: originalData.property_documents?.fire_dept_noc?.length > 0,
          airportClearanceExist: originalData.property_documents?.airport_clearance?.length > 0,
          sanctionPlanOfBuildingExist: originalData.property_documents?.sanction_plan_building?.length > 0,
          bankDocumentsifLoansExist: originalData.property_documents?.bank_loan_documents?.length > 0,
          legalDocumentsifLitigationExist: originalData.property_documents?.legal_documents?.length > 0,
          electricityBillExist: originalData.property_documents?.electricity_bill?.length > 0,
          rentAgreementsExist: originalData.property_documents?.rent_agreements?.length > 0,
          surveySettlementRecordsExist: originalData.property_documents?.survey_settlement_records?.length > 0,
          possessionCertificateExist: originalData.property_documents?.possession_certificate?.length > 0,
          nOCFromSocietyBuilderExist: originalData.property_documents?.noc_from_society_builder?.length > 0,
          insuranceDocumentsExist: originalData.property_documents?.insurance_documents?.length > 0,

          // Contact details
          contactName: originalData.owner_name || null,
          phoneNumber: originalData.owner_phone_number || null,
          email: originalData.owner_email_id || null,
          caretakerIsOwner: originalData.caretaker_is_owner === '1' || originalData.caretaker_is_owner === true,
          caretakerName: originalData.caretaker_name || null,
          caretakerPhoneNumber: originalData.caretaker_phone_number || null,
          caretakerEmail: originalData.caretaker_email_id || null,

          // Status and listing type
          status: 'Draft', // Always set to draft for duplicates
          listingType: originalData.listing_type || '',
        };
        console.log('Duplicate payload:', duplicatePayload);

        // Call addPropertyDetail API to create the duplicate
        console.log('Making API call to addPropertyDetail...');
        const duplicateResponse = await UseAxios(addPropertyDetail(duplicatePayload));
        console.log('API response received:', duplicateResponse);

        if (!duplicateResponse || !duplicateResponse.data.data) {
          throw new Error(duplicateResponse?.data?.message || 'Failed to create property duplicate');
        }

        // Get the new property ID from the response
        const newPropertyId = duplicateResponse.data.data?.property_id;
        if (!newPropertyId) {
          throw new Error('No property ID returned from duplicate creation');
        }

        console.log('New property created with ID:', newPropertyId);

        // Now upload and save images if any
        if (images && images.length > 0) {
          console.log(`Uploading ${images.length} images for duplicate property...`);
          try {
            const imageFormData = new FormData();
            imageFormData.append('systemId', newPropertyId);
            images.forEach((img) => {
              if (img.file instanceof File) {
                imageFormData.append('images', img.file, img.name);
                console.log(`Added image: ${img.name}`);
              }
            });
            imageFormData.append('type', 'Photos');
            const imageUploadResponse = await UseAxios(uploadFiles(imageFormData));
            console.log('Image upload response:', imageUploadResponse);
            if (imageUploadResponse?.data?.receiptData && imageUploadResponse.data.receiptData.length > 0) {
              const imageMetaDataPayload = {
                imageSave: true,
                property_id: newPropertyId,
                imageMetaData: imageUploadResponse.data.receiptData,
              };
              const imageSaveResponse = await UseAxios(addPropertyDetail(imageMetaDataPayload));
              console.log('Image save response:', imageSaveResponse);
              console.log('Images saved successfully for duplicate property');
            } else {
              console.warn('No receipt data received for images');
            }
          } catch (imageError) {
            console.error('Error uploading/saving images:', imageError);
            // Continue with document uploads even if image upload fails
          }
        }

        // Upload and save videos if any (similar to images)
        if (originalData.property_videos && originalData.property_videos.length > 0) {
          console.log('Videos would be handled similarly to images...');
          // Video handling would go here if videos were fetched
        }

        // Upload and save documents if any
        const uploadDocumentTypes = Object.keys(documents).filter(key => documents[key].length > 0);
        for (const documentType of uploadDocumentTypes) {
          const documentFiles = documents[documentType];
          if (documentFiles && documentFiles.length > 0) {
            console.log(`Uploading ${documentFiles.length} files for document type: ${documentType}`);
            try {
              const docFormData = new FormData();
              docFormData.append('systemId', newPropertyId);
              docFormData.append('type', 'Files');
              docFormData.append('subType', documentType);
              documentFiles.forEach((doc) => {
                if (doc.file instanceof File) {
                  docFormData.append('images', doc.file, doc.name);
                  console.log(`Added file: ${doc.name} for document type: ${documentType}`);
                }
              });

              const docUploadResponse = await UseAxios(uploadFiles(docFormData));
              console.log(`Upload response for ${documentType}:`, docUploadResponse);
              if (docUploadResponse?.data?.receiptData && docUploadResponse.data.receiptData.length > 0) {
                const docMetaDataPayload = {
                  property_id: newPropertyId,
                  documentSave: true,
                  documentType: documentType,
                  documentMetaData: docUploadResponse.data.receiptData,
                };
                const saveResponse = await UseAxios(addPropertyDetail(docMetaDataPayload));
                console.log(`Save response for ${documentType}:`, saveResponse);
                console.log(`Document type "${documentType}" saved successfully for duplicate property`);
              } else {
                console.warn(`No receipt data received for document type: ${documentType}`);
              }
            } catch (docError) {
              console.error(`Error uploading/saving documents for type "${documentType}":`, docError);
              // Continue with other document types even if one fails
            }
          }
        }
        console.log('Property duplicated successfully with all files!');
        // Clean up blob URLs
        images.forEach(img => {
          if (img.url) URL.revokeObjectURL(img.url);
        });
        Object.values(documents).forEach(docArray => {
          docArray.forEach(doc => {
            if (doc.url) URL.revokeObjectURL(doc.url);
          });
        });
        // Reload the properties list to show the new duplicate
        await loadProperties();
        // Show success message
        alert('Property duplicated successfully!');
      }
    } catch (error) {
      console.error('Failed to duplicate property:', error);
      // Clean up any blob URLs that were created
      if (images && Array.isArray(images)) {
        images.forEach(img => {
          if (img && img.url) URL.revokeObjectURL(img.url);
        });
      }
      if (documents && typeof documents === 'object') {
        Object.values(documents).forEach(docArray => {
          if (Array.isArray(docArray)) {
            docArray.forEach(doc => {
              if (doc && doc.url) URL.revokeObjectURL(doc.url);
            });
          }
        });
      }
      alert(`Failed to duplicate property: ${error.message || 'Unknown error'}`);
    }
  };
  const handleDelete = async (property) => {
    try {
      // Show confirmation dialog
      const confirmDelete = window.confirm(`Are you sure you want to delete property "${property.property_name || property.property_id}"? This action cannot be undone.`);
      if (!confirmDelete) {
        return; // User cancelled the deletion
      }

      // Call the delete API
      const response = await fetchData(() => UseAxios(deletePropertyDetail({
        property_id: property.property_id,
      })), {
        successMessage: 'Property deleted successfully',
      });

      console.log('Delete response:', response);

      // Reload the properties list to reflect the deletion
      await loadProperties();
    } catch (error) {
      console.error('Failed to delete property:', error);
      alert(`Failed to delete property: ${error.message || 'Unknown error'}`);
    }
  };

  // Create the map click handler using the extracted function
  const handlePropertyMapClick = createHandlePropertyMapClick(properties, handleEdit, handleView);

  // Handle add new property navigation
  const handleAddNewProperty = () => {
    navigate('/property-details');
  };

  return (
    <div className='property-dashboard-wrap'>

      {/* Header */}
      <header className='dashboard-top'>
        <div className='title-block'>
          <h1 className='page-title'>My Properties</h1>
          <p className='page-sub'>Manage your property listings and track their performance</p>
        </div>
        <div className='actions-right'>
          <button className='add-property' onClick={handleAddNewProperty}>+ Add New Property</button>
        </div>
      </header>

      {/* Summary */}
      <section className='summary-row'>
        <div className='summary-card total'>
          <div className='summary-icon'><HomeIcon /></div>
          <div className='summary-info'>
            <div className='summary-label'>Total Properties</div>
            <div className='summary-count'>{counts.total}</div>
          </div>
        </div>
        <div className='summary-card submitted'>
          <div className='summary-icon'><HomeIcon /></div>
          <div className='summary-info'>
            <div className='summary-label'>Submitted</div>
            <div className='summary-count'>{counts.submitted}</div>
          </div>
        </div>
        <div className='summary-card draft'>
          <div className='summary-icon'><HomeIcon /></div>
          <div className='summary-info'>
            <div className='summary-label'>Drafts</div>
            <div className='summary-count'>{counts.draft}</div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className='controls-row'>
        <div className='search-wrap'>
          <input
            className='search-input'
            placeholder="Search properties by name, ID, location or contact"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className='filters-and-toggles'>
          <div className='filter-dropdowns'>
            <select
              className='filter-dropdown'
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="All Types">All Types</option>
              <option value="Residential">Residential</option>
              <option value="Agricultural">Agricultural</option>
              <option value="Commercial">Commercial</option>
            </select>
            <select
              className='filter-dropdown'
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="All Status">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Submitted">Submitted</option>
            </select>
          </div>
          <div className='view-toggles'>
            <button className={`toggle ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>☐</button>
            <button className={`toggle ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰</button>
            <button className={`toggle ${viewMode === 'map' ? 'active' : ''}`} onClick={() => setViewMode('map')}>
              <MapIcon fontSize='small' />
            </button>
          </div>
        </div>
      </section>

      <div className='results-info'>
        Showing {visibleProperties.length} of {viewMode === 'map' ? visibleProperties.length : properties.length} properties
      </div>

      {/* Properties */}
      <section className={`properties ${viewMode === 'list' ? 'list-mode' : viewMode === 'map' ? 'map-mode' : 'grid-mode'}`}>
        {viewMode === 'map' ? (
          isLoadingMapProperties && mapFilteredProperties.length === 0 ? (
            <div className='map-loading'>Loading properties for map...</div>
          ) : (
            <PropertyMapView
              properties={mapSafeProperties}
              onViewDetails={handleView}
              isLoggedIn={Boolean(user)}
              user={user}
            />
          )
        ) : (
          <>
            {filteredProperties.length === 0 && !isLoadingProperties ? (
              <div className='no-results'>No properties found</div>
            ) : (
              filteredProperties.map((propertyItem) => (
                <PropertyCard
                  key={propertyItem.property_id || propertyItem.id}
                  property={propertyItem}
                  viewMode={viewMode}
                  onEdit={handleEdit}
                  onView={handleView}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              ))
            )}
          </>
        )}
      </section>

      {/* Error */}
      {error && (
        <div className='error-area'>
          <div className='error-text'>{error}</div>
          <button onClick={clearError}>Dismiss</button>
        </div>
      )}
    </div>
  );
};

export default PropertyList;
